import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import permissionsService from '../../services/permissionsService';
import api from '../../services/api';
import FileUploadZone from './FileUploadZone';
import PageHeader from '../Shared/PageHeader';
import { FormSection, ToggleRow } from '../ui';

import { 
  ArrowLeft, 
  ArrowRight, 
  Globe, 
  Building2, 
  FlaskConical,
  FileText,
  Brain,
  BarChart3,
  Clock,
  CheckCircle2,
  Upload,
  Edit3,
  Copy,
  Languages,
  Smartphone,
  Eye,
  X,
  Layers,
  ListChecks,
  Loader2,
  FilePlus
} from 'lucide-react';
// Numbered section title, so the form reads as a short sequence of steps.
const StepTitle = ({ n, children }) => (
  <span className="flex items-center gap-2">
    <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary-50 text-[11px] font-bold tabular-nums text-primary-700 ring-1 ring-inset ring-primary-200 dark:bg-primary-900/30 dark:text-primary-300 dark:ring-primary-800/50">
      {n}
    </span>
    <span>{children}</span>
  </span>
);


const LibrarySelection = ({ onNext, onBack, fieldErrors, onClearFieldError }) => {
  const navigate = useNavigate();
  const { user, userPermissions } = useContext(AuthContext);
  
  // Primary/Variant selection
  const [variantMode, setVariantMode] = useState('primary'); // 'primary' or 'variant'
  const [primaryAssessments, setPrimaryAssessments] = useState([]);
  const [loadingPrimaries, setLoadingPrimaries] = useState(false);
  const [selectedPrimary, setSelectedPrimary] = useState('');
  const [devices, setDevices] = useState({ phone: [], tablet: [], desktop: [] });
  const [selectedDevice, setSelectedDevice] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [variantName, setVariantName] = useState('');
  const [variantError, setVariantError] = useState('');
  const [copyStructure, setCopyStructure] = useState(true);
  
  const [selectedLibrary, setSelectedLibrary] = useState('');
  const [creationMethod, setCreationMethod] = useState('manual'); // 'manual' or 'import'
  const [assessmentType, setAssessmentType] = useState('fixed_form');
  const [assessmentName, setAssessmentName] = useState('');
  const [description, setDescription] = useState('');
  const [therapeuticArea, setTherapeuticArea] = useState('');
  // FECOA-1526: respondent type chosen at creation (PRO / ObsRO / ClinRO).
  const [respondentType, setRespondentType] = useState('');
  const [respondentTypeError, setRespondentTypeError] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [selectedStudy, setSelectedStudy] = useState('');
  const [availableStudies, setAvailableStudies] = useState([]);
  const [loadingStudies, setLoadingStudies] = useState(false);
  const [availableAssessments, setAvailableAssessments] = useState([]);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [selectedSourceAssessment, setSelectedSourceAssessment] = useState('');
  const [canPublishToVPG, setCanPublishToVPG] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Duplicate name handling
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [duplicateNameData, setDuplicateNameData] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  
  // Local name error state (for inline validation display)
  const [nameError, setNameError] = useState('');

  // FECOA-1995: Copy from Existing prefills the Assessment Information block
  // from the source. Fields the user has typed into are never overwritten when
  // the source selection changes, so their edits survive a change of mind.
  const copyFieldsTouched = useRef({
    name: false,
    therapeuticArea: false,
    description: false,
    respondentType: false,
  });

  const libraryOptions = [
    {
      id: 'vpg',
      title: 'VPG Global Library',
      icon: Globe,
      description: 'Available to all sponsors and studies',
      available: canPublishToVPG,
      tooltip: canPublishToVPG ? '' : 'Requires special permission to publish to VPG library'
    },
    {
      id: 'sponsor', 
      title: 'Sponsor Library',
      icon: Building2,
      description: 'Available to all studies under your sponsor',
      available: true,
      tooltip: ''
    },
    {
      id: 'study',
      title: 'Study-Specific Library', 
      icon: FlaskConical,
      description: 'Available only within selected study',
      available: true,
      tooltip: ''
    }
  ];

  const assessmentTypes = [
    {
      id: 'fixed_form',
      title: 'Fixed Form',
      icon: FileText,
      description: 'Traditional static questionnaire with predetermined items',
      recommended: 'Most common choice'
    },
    {
      id: 'item_bank',
      title: 'Item Library',
      icon: BarChart3,
      description: 'Create a short form from a library of items',
      recommended: 'Flexible item selection'
    }
  ];

  const commonLanguages = [
    { code: 'en-US', label: 'English (US)' },
    { code: 'es-ES', label: 'Spanish (Spain)' },
    { code: 'es-MX', label: 'Spanish (Mexico)' },
    { code: 'fr-FR', label: 'French (France)' },
    { code: 'de-DE', label: 'German (Germany)' },
    { code: 'it-IT', label: 'Italian (Italy)' },
    { code: 'pt-BR', label: 'Portuguese (Brazil)' },
    { code: 'ja-JP', label: 'Japanese (Japan)' },
    { code: 'zh-CN', label: 'Chinese (Simplified)' },
    { code: 'ko-KR', label: 'Korean (South Korea)' }
  ];

  // Check VPG library permission on mount
  useEffect(() => {
    const checkVPGPermission = async () => {
      const hasPermission = await permissionsService.hasPermission(
        permissionsService.PERMISSIONS.PUBLISH_TO_VPG_LIBRARY
      );
      setCanPublishToVPG(hasPermission);
    };
    checkVPGPermission();
  }, []);

  // Fetch devices and primaries on mount
  useEffect(() => {
    if (variantMode === 'variant') {
      fetchPrimaries();
      fetchDevices();
    }
  }, [variantMode]);

  const fetchPrimaries = async () => {
    setLoadingPrimaries(true);
    try {
      const response = await api.get('/api/assessment-builder/assessments/');
      const data = response.data;
      const primaries = (data.results || data || []).filter(a => !a.parent_assessment);
      setPrimaryAssessments(primaries);
    } catch (error) {
      console.error('Error fetching primary assessments:', error);
    } finally {
      setLoadingPrimaries(false);
    }
  };

  const fetchDevices = async () => {
    try {
      const response = await api.get('/api/assessment-builder/devices/');
      const data = response.data;
      setDevices(data.grouped || { phone: [], tablet: [], desktop: [] });
    } catch (error) {
      console.error('Error fetching devices:', error);
    }
  };

  // Fetch assessments that can be copied
  const fetchAssessments = async () => {
    setLoadingAssessments(true);
    try {
      const response = await api.get('/api/assessment-builder/assessments/');
      const data = response.data;
      let assessments = [];
      if (Array.isArray(data)) {
        assessments = data;
      } else if (data.results && Array.isArray(data.results)) {
        assessments = data.results;
      } else if (data.data && Array.isArray(data.data)) {
        assessments = data.data;
      }
      
      // Show all assessments (will be grouped by status in dropdown)
      setAvailableAssessments(assessments);
    } catch (error) {
      console.error('Error fetching assessments:', error);
      setAvailableAssessments([]);
    } finally {
      setLoadingAssessments(false);
    }
  };

  // Fetch studies that the current user has access to
  const fetchStudies = async () => {
    setLoadingStudies(true);
    try {
      const response = await api.get('/api/studies/');
      const data = response.data;
      
      // Handle different response formats
      let studies = [];
      if (Array.isArray(data)) {
        studies = data;
      } else if (data.results && Array.isArray(data.results)) {
        studies = data.results;
      } else if (data.data && Array.isArray(data.data)) {
        studies = data.data;
      } else {
        console.warn('Unexpected studies response format:', data);
      }
      
      setAvailableStudies(studies);
    } catch (error) {
      console.error('Error fetching studies:', error);
      setAvailableStudies([]);
    } finally {
      setLoadingStudies(false);
    }
  };

  // Update validation logic to include study selection for study-specific library and variant
  const canProceed = variantMode === 'variant' 
    ? (selectedPrimary && selectedDevice && selectedLanguage)
    : (
      selectedLibrary && (
        selectedLibrary !== 'study' || selectedStudy
      ) && (
        (creationMethod === 'manual' && assessmentName.trim() && assessmentType) ||
        (creationMethod === 'import' && uploadedFile) ||
        (creationMethod === 'copy' && selectedSourceAssessment && assessmentName.trim())
      )
    );

  // What still stands between the user and Continue (same checks as canProceed).
  const missingItems = variantMode === 'variant'
    ? [
        !selectedPrimary && 'the primary assessment',
        !selectedDevice && 'a device',
        !selectedLanguage && 'a language',
      ].filter(Boolean)
    : [
        !selectedLibrary && 'a library location',
        selectedLibrary === 'study' && !selectedStudy && 'a study',
        creationMethod === 'manual' && !assessmentName.trim() && 'an assessment name',
        creationMethod === 'manual' && !assessmentType && 'an assessment type',
        creationMethod === 'copy' && !selectedSourceAssessment && 'an assessment to copy',
        creationMethod === 'copy' && !assessmentName.trim() && 'a name for the copy',
        creationMethod === 'import' && !uploadedFile && 'a file to import',
      ].filter(Boolean);
  const listItems = (items) =>
    items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

  const handleFileUpload = async (file) => {
    setUploadError('');
    
    // Validate study selection for study-specific library
    if (selectedLibrary === 'study' && !selectedStudy) {
      setUploadError('Please select a study before uploading a CDISC file.');
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('library_type', selectedLibrary);
      if (selectedLibrary === 'study' && selectedStudy) {
        formData.append('study_id', selectedStudy);
      }
      
      console.log('[CDISC IMPORT] Uploading file - format and version will be auto-detected');
      
      const response = await api.post('/api/assessment-builder/import/cdisc/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const result = response.data;
      
      console.log('[CDISC IMPORT] Import successful:', result);
      
      // Success! Navigate directly to completion or next step
      if (onNext) {
        onNext({
          mode: 'import',
          assessment_id: result.assessment_id,
          assessment_uuid: result.assessment_uuid || result.uuid,
          survey_id: result.survey_id,
          name: result.name,
          library_type: selectedLibrary,
          items_imported: result.items_imported,
          imported_items: result.imported_items || [],
          detected_version: result.detected_odm_version || result.cdisc_version
        });
      }
      
    } catch (error) {
      console.error('[CDISC IMPORT] Upload error:', error);
      
      // Check for duplicate name error (409 Conflict)
      if (error.response?.status === 409 && error.response?.data?.error_code === 'DUPLICATE_NAME') {
        const data = error.response.data;
        const userProvidedName = assessmentName && assessmentName.trim();
        setDuplicateNameData({
          file,
          originalName: data.original_name,
          suggestedName: data.suggested_name,
          errorMessage: data.error,
          userProvidedName: userProvidedName // Track if user already provided a name
        });
        setRenameValue(data.suggested_name || `${data.original_name} (Copy)`);
        setShowRenameModal(true);
        setUploadError('');
      } else {
        setUploadError(error.response?.data?.error || error.message);
      }
    } finally {
      setUploading(false);
    }
  };
  
  // Retry import with new name after duplicate detected
  const handleRetryWithNewName = async () => {
    if (!duplicateNameData?.file || !renameValue.trim()) return;
    
    setShowRenameModal(false);
    setUploading(true);
    setUploadError('');
    
    try {
      const formData = new FormData();
      formData.append('file', duplicateNameData.file);
      formData.append('library_type', selectedLibrary);
      formData.append('name', renameValue.trim());
      if (selectedLibrary === 'study' && selectedStudy) {
        formData.append('study_id', selectedStudy);
      }
      
      console.log('[CDISC IMPORT] Retrying with new name:', renameValue.trim());
      
      const response = await api.post('/api/assessment-builder/import/cdisc/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      const result = response.data;
      console.log('[CDISC IMPORT] Import successful with new name:', result);
      
      setDuplicateNameData(null);
      setRenameValue('');
      
      if (onNext) {
        onNext({
          mode: 'import',
          assessment_id: result.assessment_id,
          assessment_uuid: result.assessment_uuid || result.uuid,
          survey_id: result.survey_id,
          name: result.name,
          library_type: selectedLibrary,
          items_imported: result.items_imported,
          imported_items: result.imported_items || [],
          detected_version: result.detected_odm_version || result.cdisc_version
        });
      }
    } catch (error) {
      console.error('[CDISC IMPORT] Retry error:', error);
      
      // Check if still a duplicate name error
      if (error.response?.status === 409 && error.response?.data?.error_code === 'DUPLICATE_NAME') {
        setRenameValue(error.response.data.suggested_name || `${renameValue} (Copy)`);
        setShowRenameModal(true);
        setUploadError('That name is also taken. Please try another.');
      } else {
        setUploadError(error.response?.data?.error || error.message);
        setDuplicateNameData(null);
      }
    } finally {
      setUploading(false);
    }
  };

  const fetchAssessmentPreview = async (assessmentId) => {
    setLoadingPreview(true);
    setPreviewData(null);
    
    try {
      const response = await api.get(`/api/assessment-builder/assessments/${assessmentId}/`);
      setPreviewData(response.data);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Error fetching assessment preview:', error);
      setUploadError('Failed to load assessment preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  // FECOA-1995: picking a source assessment seeds name, COA type, therapeutic
  // area and description from it. The list endpoint already returns all four,
  // so no extra request is needed.
  const handleSourceAssessmentChange = (uuid) => {
    setSelectedSourceAssessment(uuid);
    if (!uuid) return;

    const source = availableAssessments.find(a => a.uuid === uuid);
    if (!source) return;

    const touched = copyFieldsTouched.current;
    if (!touched.name) {
      setAssessmentName(source.name ? `Copy of ${source.name}` : '');
      setNameError('');
      setUploadError('');
    }
    if (!touched.respondentType && source.respondent_type) {
      setRespondentType(source.respondent_type);
      setRespondentTypeError('');
    }
    if (!touched.therapeuticArea) {
      setTherapeuticArea(source.therapeutic_area || '');
    }
    if (!touched.description) {
      setDescription(source.description || '');
    }
  };

  const handleCopyAssessment = async () => {
    if (!selectedSourceAssessment || !assessmentName.trim()) {
      return;
    }
    // FECOA-1526: respondent type is required when copying too.
    if (!respondentType) {
      setRespondentTypeError('Please select a COA type.');
      return;
    }

    try {
      setUploading(true);
      setUploadError('');

      const payload = {
        name: assessmentName.trim(),
        library_type: selectedLibrary,
        description: description.trim(),
        respondent_type: respondentType,
        // FECOA-1996: the form collects this, so it has to be sent — otherwise
        // the copy silently inherits the source's therapeutic area.
        therapeutic_area: therapeuticArea.trim(),
      };

      // Include study_id for study-specific assessments
      if (selectedLibrary === 'study' && selectedStudy) {
        payload.study_id = selectedStudy;
      }

      const response = await api.post(`/api/assessment-builder/assessments/${selectedSourceAssessment}/copy/`, payload);
      const copiedAssessment = response.data;
      
      // Navigate to screen builder with the copied assessment
      if (onNext) {
        onNext({
          mode: 'edit',
          assessment_id: copiedAssessment.id,
          assessment_uuid: copiedAssessment.uuid,
          survey_id: copiedAssessment.survey_id || copiedAssessment.survey?.id,
          name: copiedAssessment.name,
          library_type: copiedAssessment.library_type,
          assessment_type: copiedAssessment.assessment_type
        });
      }
    } catch (error) {
      console.error('Copy error:', error);
      // Extract error message from response data
      const errorData = error.response?.data;
      let errorMessage = 'Failed to copy assessment';
      if (errorData) {
        if (errorData.name) {
          errorMessage = errorData.name;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      }
      setUploadError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  // Reject rapid successive submits. State alone can't catch taps that arrive
  // within the same React tick, so the ref lets us no-op the second call
  // before any network request fires.
  const submitInFlightRef = useRef(false);

  const handleNext = async () => {
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    // Clear previous errors
    setVariantError('');

    // Handle variant creation
    if (variantMode === 'variant') {
      setUploading(true);
      try {
        const requestBody = {
          device_id: parseInt(selectedDevice),
          language: selectedLanguage,
          copy_structure: copyStructure
        };
        
        // Only include variant_name if provided
        if (variantName && variantName.trim()) {
          requestBody.variant_name = variantName.trim();
        }
        
        const response = await api.post(
          `/api/assessment-builder/assessments/${selectedPrimary}/create-variant/`,
          requestBody
        );

        const data = response.data;
        console.log('✅ Variant created:', data);
        
        // Get the variant assessment details and call onNext
        const variantResponse = await api.get(`/api/assessment-builder/assessments/${data.variant_assessment_uuid || data.variant_assessment}/`);
        const variantData = variantResponse.data;
        const config = {
          assessment_id: variantData.id,
          assessment_uuid: variantData.uuid,
          survey_id: variantData.survey?.id || variantData.survey,
          name: variantData.name,
          library_type: variantData.library_type,
          assessment_type: variantData.assessment_type,
          mode: 'edit',
          ...variantData.builder_metadata
        };
        if (onNext) {
          onNext(config);
        }
      } catch (error) {
        console.error('❌ Error creating variant:', error);
        // Handle error response from api
        const errorData = error.response?.data;
        let errorMessage = errorData?.error || errorData?.message || errorData?.non_field_errors?.[0] || error.message || 'Failed to create variant';
        if (typeof errorMessage !== 'string') {
          errorMessage = JSON.stringify(errorMessage) || 'Failed to create variant';
        }
        
        if (errorMessage.toLowerCase().includes('already exists')) {
          setVariantError(
            `A variant for ${selectedLanguage} on this device already exists. ` +
            'Please enter a custom name below to create another variant with the same language/device combination.'
          );
        } else {
          setVariantError(errorMessage);
        }
      } finally {
        setUploading(false);
        submitInFlightRef.current = false;
      }
      return;
    }

    // Handle copy method
    if (creationMethod === 'copy') {
      try {
        await handleCopyAssessment();
      } finally {
        submitInFlightRef.current = false;
      }
      return;
    }

    // Handle manual creation - make API call directly to handle errors inline
    setNameError(''); // Clear previous errors
    // FECOA-1526: respondent type is required at creation.
    if (!respondentType) {
      setRespondentTypeError('Please select a COA type.');
      return;
    }
    setUploading(true);

    try {
      const payload = {
        survey_data: {
          name: assessmentName.trim(),
          language: 'en-US',
          description: description.trim(),
          long_description: '',
          respondent_type: respondentType
        },
        library_type: selectedLibrary,
        assessment_type: assessmentType,
        therapeutic_area: therapeuticArea.trim(),
        version: '1.0',
        builder_metadata: {}
      };

      // Include study_id for study-specific assessments
      if (selectedLibrary === 'study' && selectedStudy) {
        payload.study_id = selectedStudy;
      }

      console.log('Creating new assessment:', payload);
      const response = await api.post('/api/assessment-builder/assessments/', payload);
      const newAssessment = response.data;
      console.log('Created assessment:', newAssessment);

      // Success - call parent's onNext with the created assessment.
      // assessment_uuid MUST be forwarded — the parent navigates to
      // /assessment-builder/{uuid} and a missing uuid produces a "undefined"
      // route param, which triggers a bogus GET /assessments/undefined/ 400.
      if (onNext) {
        onNext({
          mode: 'edit',
          assessment_id: newAssessment.id,
          assessment_uuid: newAssessment.uuid,
          survey_id: newAssessment.survey_id || newAssessment.survey?.id,
          name: newAssessment.name || assessmentName.trim(),
          library_type: selectedLibrary,
          assessment_type: assessmentType,
          studies: newAssessment.studies || [],
          version: newAssessment.version,
          publication_status: newAssessment.publication_status,
          default_templates: newAssessment.default_templates,
        });
      }
    } catch (error) {
      console.error('Error creating assessment:', error);
      console.error('Error response data:', error.response?.data);
      
      const errorData = error.response?.data;
      const nameErr = errorData?.name || errorData?.survey_data?.name;
      
      if (nameErr) {
        // Show inline error for duplicate name
        const errorMessage = Array.isArray(nameErr) ? nameErr[0] : nameErr;
        console.log('Setting name error:', errorMessage);
        setNameError(errorMessage);
      } else {
        // Generic error
        const errorMsg = errorData?.error || errorData?.detail || error.message;
        setUploadError(`Failed to create assessment: ${errorMsg}`);
      }
    } finally {
      setUploading(false);
      submitInFlightRef.current = false;
    }
  };

  // Shared looks for the option tiles and notices below (FECOA-2174)
  const tileClass = (isSelected) => `relative cursor-pointer rounded-xl border p-4 transition-colors ${
    isSelected
      ? 'border-primary-500 bg-primary-50/40 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-400/10 dark:ring-primary-400'
      : 'border-gray-200/80 bg-white shadow-card hover:border-gray-300 dark:border-white/[0.07] dark:bg-gray-800 dark:hover:border-white/15'
  }`;
  const tileIconClass = (isSelected) => `h-[18px] w-[18px] flex-shrink-0 ${
    isSelected ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'
  }`;
  const tileCheckClass = 'h-[18px] w-[18px] flex-shrink-0 text-primary-600 dark:text-primary-400';
  const noticeClass = 'rounded-lg border border-gray-200/70 bg-gray-50 p-3.5 text-[12.5px] leading-relaxed text-gray-600 dark:border-white/[0.07] dark:bg-white/[0.03] dark:text-gray-300';
  const warningClass = 'rounded-lg border border-amber-200 bg-amber-50 p-3.5 text-[12.5px] leading-relaxed text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200';
  const errorClass = 'rounded-lg border border-red-200 bg-red-50 p-3.5 text-[13px] leading-relaxed text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300';
  const spinnerClass = 'h-4 w-4 flex-shrink-0 animate-spin text-primary-600 dark:text-primary-400';

  // Step numbers follow the sections actually shown.
  let stepCount = 0;
  const nextStep = () => {
    stepCount += 1;
    return stepCount;
  };

  return (
    <div>
      <PageHeader
        icon={FilePlus}
        title="Create New Assessment"
        subtitle={(
          <>
            <span className="font-medium text-gray-700 dark:text-gray-200">Configure Assessment Settings</span>
            <span aria-hidden="true" className="mx-1.5 text-gray-300 dark:text-gray-600">·</span>
            Choose where to store your assessment and how you'd like to create it
          </>
        )}
      />

      <div className="max-w-5xl">
        <div className="divide-y divide-gray-100 dark:divide-white/[0.06]">
          {/* Primary/Variant Mode Toggle */}
          {/* FECOA-1526: relabeled from "Assessment Type" to match the mockup */}
          <FormSection title={<StepTitle n={nextStep()}>Assessment Creation Mode *</StepTitle>}>
            <div className="segmented flex w-full sm:inline-flex sm:w-auto">
              <button
                type="button"
                onClick={() => {
                  setVariantMode('primary');
                  setVariantError('');
                }}
                className={`segmented-item flex-1 justify-center sm:flex-none ${
                  variantMode === 'primary'
                    ? 'segmented-item-active'
                    : ''
                }`}
              >
                <FileText className="h-4 w-4" />
                Create Primary Assessment
              </button>
              <button
                type="button"
                onClick={() => {
                  setVariantMode('variant');
                  setVariantError('');
                }}
                className={`segmented-item flex-1 justify-center sm:flex-none ${
                  variantMode === 'variant'
                    ? 'segmented-item-active'
                    : ''
                }`}
              >
                <Languages className="h-4 w-4" />
                Create Variant
              </button>
            </div>
            {variantMode === 'variant' && (
              <div className={`mt-4 ${noticeClass}`}>
                <div className="space-y-1.5">
                  <p className="flex items-center gap-2 font-medium text-gray-800 dark:text-gray-100">
                    <Languages className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                    Variants are linked to their primary assessment
                  </p>
                  <p>
                    Use variants to create language translations or device-specific versions that maintain a connection to the original primary assessment. Ideal for multi-language studies or platform adaptations.
                  </p>
                  <p className="text-gray-500 dark:text-gray-400">
                    Looking to create an independent copy? Use "Create Primary" → "Copy from Existing" instead.
                  </p>
                </div>
              </div>
            )}
          </FormSection>

          {/* Conditionally render Primary flow or Variant form */}
          {variantMode === 'primary' ? (
            <>
              {/* Library Selection */}
              <FormSection title={<StepTitle n={nextStep()}>Choose Library Location *</StepTitle>}>
                <div className="grid gap-3 sm:auto-cols-fr sm:grid-flow-col">
                  {libraryOptions.filter(option => option.available).map((option) => {
                    const IconComponent = option.icon;
                    const isSelected = selectedLibrary === option.id;

                    return (
                      <div
                        key={option.id}
                        onClick={() => {
                          setSelectedLibrary(option.id);
                          setSelectedStudy('');
                          if (option.id === 'study') {
                            fetchStudies();
                          }
                        }}
                        className={tileClass(isSelected)}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <IconComponent className={tileIconClass(isSelected)} />
                          {isSelected && (
                            <CheckCircle2 className={tileCheckClass} />
                          )}
                        </div>
                        <h4 className="mt-3 text-[13.5px] font-medium text-gray-900 dark:text-gray-50">{option.title}</h4>
                        <p className="mt-0.5 text-[12.5px] leading-relaxed text-gray-500 dark:text-gray-400">{option.description}</p>
                      </div>
                    );
                  })}
                </div>

                {/* Study Selection */}
                {selectedLibrary === 'study' && (
                  <div className="mt-5">
                    <label className="label">
                      Select Study *
                    </label>
                    {loadingStudies ? (
                      <div className="flex items-center gap-2 text-[13px] text-gray-500 dark:text-gray-400">
                        <Loader2 className={spinnerClass} />
                        <span>Loading studies...</span>
                      </div>
                    ) : (
                      <select
                        value={selectedStudy}
                        onChange={(e) => setSelectedStudy(e.target.value)}
                        className="input"
                      >
                        <option value="">Choose a study...</option>
                        {availableStudies.map((study) => (
                          <option key={study.id} value={study.id}>
                            {study.study_code} {study.title}
                          </option>
                        ))}
                      </select>
                    )}
                    {availableStudies.length === 0 && !loadingStudies && (
                      <div className={`mt-2 ${warningClass}`}>
                        <p className="font-medium">No studies available</p>
                        <p className="mt-0.5">
                          Contact your administrator for access, or choose a different library type.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </FormSection>

              {/* Creation Method Selection */}
              <FormSection title={<StepTitle n={nextStep()}>How would you like to create this assessment? *</StepTitle>}>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div
                    onClick={() => setCreationMethod('manual')}
                    className={tileClass(creationMethod === 'manual')}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <Edit3 className={tileIconClass(creationMethod === 'manual')} />
                      {creationMethod === 'manual' && (
                        <CheckCircle2 className={tileCheckClass} />
                      )}
                    </div>
                    <h4 className="mt-3 text-[13.5px] font-medium text-gray-900 dark:text-gray-50">Build from Scratch</h4>
                    <p className="mt-0.5 text-[12.5px] leading-relaxed text-gray-500 dark:text-gray-400">Use the visual builder to create screens manually</p>
                  </div>

                  <div
                    onClick={() => {
                      setCreationMethod('copy');
                      fetchAssessments();
                    }}
                    className={tileClass(creationMethod === 'copy')}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <Copy className={tileIconClass(creationMethod === 'copy')} />
                      {creationMethod === 'copy' && (
                        <CheckCircle2 className={tileCheckClass} />
                      )}
                    </div>
                    <h4 className="mt-3 text-[13.5px] font-medium text-gray-900 dark:text-gray-50">Copy from Existing</h4>
                    <p className="mt-0.5 text-[12.5px] leading-relaxed text-gray-500 dark:text-gray-400">Creates independent copy with no link to original</p>
                  </div>

                  <div
                    onClick={() => setCreationMethod('import')}
                    className={tileClass(creationMethod === 'import')}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <Upload className={tileIconClass(creationMethod === 'import')} />
                      {creationMethod === 'import' && (
                        <CheckCircle2 className={tileCheckClass} />
                      )}
                    </div>
                    <h4 className="mt-3 text-[13.5px] font-medium text-gray-900 dark:text-gray-50">Import from File</h4>
                    <p className="mt-0.5 text-[12.5px] leading-relaxed text-gray-500 dark:text-gray-400">Upload a CDISC ODM XML or CSV file</p>
                  </div>
                </div>

                {/* Copy from Existing Selection */}
                {creationMethod === 'copy' && (
                  <div className="mt-5 space-y-4">
                    <div className={noticeClass}>
                      <div className="flex items-start gap-3">
                        <Copy className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                        <div className="min-w-0 flex-1">
                          <p className="mb-1 font-medium text-gray-800 dark:text-gray-100">
                            Copy from Existing Assessment
                          </p>
                          <p className="mb-2">
                            Creates a <strong className="font-semibold text-gray-800 dark:text-gray-100">completely independent copy</strong> with no link to the original. All screens, questions, and settings will be duplicated. You'll have full ownership to modify as needed.
                          </p>
                          <p className="text-gray-500 dark:text-gray-400">
                            Use this when: Starting a new custom assessment based on an existing one, or creating a standalone version that won't be affected by changes to the original.
                          </p>
                          <div className="mt-2.5 border-t border-gray-200/70 pt-2.5 dark:border-white/[0.07]">
                            <p className="text-gray-500 dark:text-gray-400">
                              <strong className="font-medium text-gray-700 dark:text-gray-200">Need a linked version instead?</strong> Use "Create Variant" for language translations or device adaptations that should stay connected to a primary.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="label">
                        Select Source Assessment *
                      </label>
                      {loadingAssessments ? (
                        <div className={`flex items-center gap-2 ${noticeClass}`}>
                          <Loader2 className={spinnerClass} />
                          <span>Loading assessments...</span>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <select
                            value={selectedSourceAssessment}
                            onChange={(e) => handleSourceAssessmentChange(e.target.value)}
                            className="input min-w-0 flex-1"
                          >
                            <option value="">Choose an assessment to copy...</option>

                          {/* Published Assessments */}
                          {availableAssessments.filter(a => a.publication_status === 'published').length > 0 && (
                            <optgroup label="Published Assessments">
                              {availableAssessments
                                .filter(a => a.publication_status === 'published')
                                .map((assessment) => (
                                  <option key={assessment.uuid} value={assessment.uuid}>
                                    {assessment.name} (v{assessment.version}) - {assessment.library_type}
                                  </option>
                                ))}
                            </optgroup>
                          )}

                          {/* Draft Assessments */}
                          {availableAssessments.filter(a => a.publication_status !== 'published').length > 0 && (
                            <optgroup label="Draft Assessments">
                              {availableAssessments
                                .filter(a => a.publication_status !== 'published')
                                .map((assessment) => (
                                  <option key={assessment.uuid} value={assessment.uuid}>
                                    {assessment.name} (v{assessment.version}) - {assessment.library_type}
                                  </option>
                                ))}
                            </optgroup>
                          )}
                          </select>

                          {/* Preview Button */}
                          {selectedSourceAssessment && (
                            <button
                              type="button"
                              onClick={() => fetchAssessmentPreview(selectedSourceAssessment)}
                              disabled={loadingPreview}
                              className="btn btn-secondary h-10"
                              title="View Assessment Summary"
                            >
                              {loadingPreview ? (
                                <>
                                  <Loader2 className={spinnerClass} />
                                  <span>Loading...</span>
                                </>
                              ) : (
                                <>
                                  <Eye className="h-4 w-4" />
                                  <span>Preview</span>
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      )}
                      {availableAssessments.length === 0 && !loadingAssessments && (
                        <div className={`mt-2 ${warningClass}`}>
                          <p className="font-medium">No assessments available</p>
                          <p className="mt-0.5">
                            Create an assessment first, then it will appear here for copying.
                          </p>
                        </div>
                      )}
                    </div>

                    {selectedSourceAssessment && (
                      <div className={noticeClass}>
                        <p className="mb-1 font-medium text-gray-800 dark:text-gray-100">
                          Provide a New Assessment Name
                        </p>
                        <p>
                          The selected assessment will be duplicated with all its screens and questions. Please provide a unique name for the new copy below.
                        </p>
                        {availableAssessments.find(a => a.uuid === selectedSourceAssessment)?.publication_status !== 'published' && (
                          <p className="mt-2 font-medium text-amber-700 dark:text-amber-300">
                            Note: You're copying a draft assessment. Consider publishing the original first if you want a stable starting point.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* File Upload Zone */}
                {creationMethod === 'import' && (
                  <div className="mt-5 space-y-4">
                    <div className={noticeClass}>
                      <div className="flex items-start gap-3">
                        <Brain className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                        <div className="min-w-0 flex-1">
                          <p className="mb-1 font-medium text-gray-800 dark:text-gray-100">
                            Assessment Information from File
                          </p>
                          <p>
                            Upload a CDISC file to automatically extract assessment details and items. The assessment name will be taken from the file.
                          </p>
                        </div>
                      </div>
                    </div>

                    {selectedLibrary === 'study' && !selectedStudy && (
                      <div className={warningClass}>
                        <p>
                          Please select a study above before uploading a CDISC file.
                        </p>
                      </div>
                    )}
                    <FileUploadZone
                      onFileSelect={setUploadedFile}
                      onUpload={handleFileUpload}
                      uploading={uploading}
                      uploadError={uploadError}
                      selectedLibrary={selectedLibrary}
                      disabled={selectedLibrary === 'study' && !selectedStudy}
                    />
                  </div>
                )}
              </FormSection>

              {/* Assessment Information */}
              {(creationMethod === 'manual' || creationMethod === 'copy') && (
                <FormSection title={<StepTitle n={nextStep()}>Assessment Information</StepTitle>}>
                  <div className="grid grid-cols-1 gap-x-4 gap-y-5 md:grid-cols-2">
                    <div>
                      <label className="label">
                        Assessment Name *
                      </label>
                      <input
                        type="text"
                        value={assessmentName}
                        onChange={(e) => {
                          copyFieldsTouched.current.name = true;
                          setAssessmentName(e.target.value);
                          // Clear error when user changes the name
                          if (uploadError && creationMethod === 'copy') {
                            setUploadError('');
                          }
                          // Clear local name error when typing
                          if (nameError) {
                            setNameError('');
                          }
                        }}
                        placeholder="e.g., PROMIS Physical Function 10a"
                        className={`input ${
                          (uploadError && creationMethod === 'copy') || nameError ? 'input-error' : ''
                        }`}
                      />
                      {/* Inline error message for duplicate name */}
                      {nameError && (
                        <p className="field-error flex items-center">
                          <svg className="mr-1.5 h-3.5 w-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {nameError}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="label">
                        Therapeutic Area
                      </label>
                      <input
                        type="text"
                        value={therapeuticArea}
                        onChange={(e) => {
                          copyFieldsTouched.current.therapeuticArea = true;
                          setTherapeuticArea(e.target.value);
                        }}
                        placeholder="e.g., Oncology, Pain Management"
                        className="input"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="label">
                        Description
                      </label>
                      <textarea
                        value={description}
                        onChange={(e) => {
                          copyFieldsTouched.current.description = true;
                          setDescription(e.target.value);
                        }}
                        placeholder="Describe the purpose and content of this assessment..."
                        rows={3}
                        className="input"
                      />
                    </div>
                    {/* FECOA-1526: COA Type (respondent type) — set at creation, locked once published */}
                    <div className="md:col-span-2">
                      <label className="label">
                        COA Type *
                      </label>
                      <select
                        value={respondentType}
                        onChange={(e) => {
                          copyFieldsTouched.current.respondentType = true;
                          setRespondentType(e.target.value);
                          if (respondentTypeError) setRespondentTypeError('');
                        }}
                        className={`input ${
                          respondentTypeError ? 'input-error' : ''
                        }`}
                      >
                        <option value="">Select COA type...</option>
                        <option value="participant">PRO</option>
                        <option value="caregiver">ObsRO</option>
                        <option value="clinician">ClinRO</option>
                      </select>
                      {respondentTypeError && (
                        <p className="field-error">{respondentTypeError}</p>
                      )}
                    </div>
                  </div>

                  {/* Error Message for Copy */}
                  {creationMethod === 'copy' && uploadError && (
                    <div className={`mt-4 ${errorClass}`}>
                      <div className="flex items-start gap-3">
                        <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium">{uploadError}</p>
                        </div>
                        <button
                          onClick={() => setUploadError('')}
                          className="rounded text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </FormSection>
              )}

              {/* Assessment Type Selection */}
              {creationMethod === 'manual' && (
                <FormSection title={<StepTitle n={nextStep()}>Assessment Type *</StepTitle>}>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {assessmentTypes.map((type) => {
                      const IconComponent = type.icon;
                      const isSelected = assessmentType === type.id;

                      return (
                        <div
                          key={type.id}
                          onClick={() => setAssessmentType(type.id)}
                          className={tileClass(isSelected)}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <IconComponent className={tileIconClass(isSelected)} />
                            {isSelected && (
                              <CheckCircle2 className={tileCheckClass} />
                            )}
                          </div>
                          <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1">
                            <h4 className="text-[13.5px] font-medium text-gray-900 dark:text-gray-50">{type.title}</h4>
                            {type.recommended && (
                              <span className="badge badge-gray">
                                {type.recommended}
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-[12.5px] leading-relaxed text-gray-500 dark:text-gray-400">{type.description}</p>
                        </div>
                      );
                    })}
                  </div>
                </FormSection>
              )}
            </>
          ) : (
            // Variant fields continue the creation mode row, so no divider above them
            <section className="!border-t-0 grid gap-4 pb-7 md:grid-cols-[200px_minmax(0,1fr)] md:gap-8">
              <div className="hidden md:block" aria-hidden="true" />
              <div className="min-w-0 space-y-5">
                {/* Select Primary Assessment */}
                <div>
                  <label className="label">
                    Select Primary Assessment *
                  </label>
                  <select
                    value={selectedPrimary}
                    onChange={(e) => setSelectedPrimary(e.target.value)}
                    className="input"
                  >
                    <option value="">Choose a primary assessment...</option>
                    {primaryAssessments.map((primary) => (
                      <option key={primary.uuid} value={primary.uuid}>
                        {primary.name} (v{primary.version})
                      </option>
                    ))}
                  </select>
                  {loadingPrimaries ? (
                    <div className="mt-2 flex items-center gap-2 text-[13px] text-gray-500 dark:text-gray-400">
                      <Loader2 className={spinnerClass} />
                      <span>Loading primary assessments...</span>
                    </div>
                  ) : primaryAssessments.length === 0 ? (
                    <p className="mt-2 text-[13px] text-amber-700 dark:text-amber-300">
                      No primary assessments available. Create a primary first.
                    </p>
                  ) : null}
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  {/* Select Device */}
                  <div>
                    <label className="label">
                      Target Device *
                    </label>
                    <select
                      value={selectedDevice}
                      onChange={(e) => setSelectedDevice(e.target.value)}
                      className="input"
                    >
                      <option value="">Select device...</option>
                      <optgroup label="Phones">
                        {devices.phone?.map((device) => (
                          <option key={device.id} value={device.id}>
                            {device.name} ({device.screen_width}×{device.screen_height})
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Tablets">
                        {devices.tablet?.map((device) => (
                          <option key={device.id} value={device.id}>
                            {device.name} ({device.screen_width}×{device.screen_height})
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Desktop">
                        {devices.desktop?.map((device) => (
                          <option key={device.id} value={device.id}>
                            {device.name} ({device.screen_width}×{device.screen_height})
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  {/* Language Selection */}
                  <div>
                    <label className="label">
                      Language *
                    </label>
                    <select
                      value={selectedLanguage}
                      onChange={(e) => setSelectedLanguage(e.target.value)}
                      className="input"
                    >
                      {commonLanguages.map((lang) => (
                        <option key={lang.code} value={lang.code}>
                          {lang.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Error Message */}
                {variantError && (
                  <div className={errorClass}>
                    <div className="flex items-start gap-3">
                      <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">
                          {variantError}
                        </p>
                      </div>
                      <button
                        onClick={() => setVariantError('')}
                        className="rounded text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Variant Name (Optional) */}
                <div>
                  <label className="label">
                    Variant Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={variantName}
                    onChange={(e) => {
                      setVariantName(e.target.value);
                      // Clear error when user starts typing a custom name
                      if (variantError && String(variantError).toLowerCase().includes('already exists')) {
                        setVariantError('');
                      }
                    }}
                    placeholder="Leave blank to auto-generate"
                    className={`input ${
                      variantError && String(variantError).toLowerCase().includes('already exists')
                        ? 'border-amber-400 focus:border-amber-500 focus:ring-amber-500/15'
                        : ''
                    }`}
                  />
                  <p className="hint">
                    If left blank, will auto-generate as: "{
                      primaryAssessments.find(p => p.uuid === selectedPrimary)?.name || 'Primary'
                    } - {selectedLanguage} ({
                      [...(devices.phone || []), ...(devices.tablet || []), ...(devices.desktop || [])]
                        .find(d => d.id === parseInt(selectedDevice))?.name || 'Device'
                    })"
                  </p>
                </div>

                {/* Copy Structure setting */}
                <ToggleRow
                  id="copy_structure"
                  title="Copy structure from primary"
                  description="Recommended: Copies all screens and questions. Uncheck to start with empty variant."
                  checked={copyStructure}
                  onChange={(e) => setCopyStructure(e.target.checked)}
                />
              </div>
            </section>
          )}
        </div>

        {/* Navigation */}
        <div className="sticky bottom-0 z-10 -mx-1 mt-2 flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 bg-gray-50/95 px-1 py-4 dark:border-gray-700 dark:bg-gray-900/95">
          {!(variantMode !== 'variant' && creationMethod === 'import') && (
            <p className="mr-auto text-[13px] text-gray-500 dark:text-gray-400" aria-live="polite">
              {missingItems.length
                ? `To continue, choose ${listItems(missingItems)}.`
                : 'Everything needed is filled in.'}
            </p>
          )}
          <button
            onClick={onBack}
            className="btn btn-secondary"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </button>

          {variantMode === 'variant' ? (
            <button
              onClick={handleNext}
              disabled={!canProceed || uploading}
              className="btn btn-primary"
            >
              {uploading ? 'Creating…' : 'Create Variant'}
              <ArrowRight className="h-4 w-4" />
            </button>
          ) : creationMethod === 'copy' ? (
            <button
              onClick={handleNext}
              disabled={!canProceed || uploading}
              className="btn btn-primary"
            >
              {uploading ? 'Copying…' : 'Create Copy'}
              <ArrowRight className="h-4 w-4" />
            </button>
          ) : creationMethod === 'manual' && (
            <button
              onClick={handleNext}
              disabled={!canProceed || uploading}
              className="btn btn-primary"
            >
              {uploading ? 'Creating…' : 'Continue to Screen Builder'}
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Assessment Preview Modal */}
      {showPreviewModal && previewData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70">
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
            {/* Modal Header */}
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4 dark:border-white/[0.06]">
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50">Assessment Preview</h2>
                <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400">View details before importing</p>
              </div>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="btn btn-ghost btn-sm btn-icon -mr-2"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
              {/* Assessment Info */}
              <div>
                <h3 className="mb-3 text-[15px] font-semibold text-gray-900 dark:text-gray-50">{previewData.name}</h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-[13px]">
                  <div className="flex items-center">
                    <span className="text-gray-500 dark:text-gray-400">Version:</span>
                    <span className="code-chip ml-2">v{previewData.version}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 dark:text-gray-400">Library:</span>
                    <span className="badge badge-gray ml-2 capitalize">{previewData.library_type}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 dark:text-gray-400">Status:</span>
                    <span className={`badge badge-dot ml-2 ${
                      previewData.publication_status === 'published'
                        ? 'badge-success'
                        : 'badge-warning'
                    }`}>
                      {previewData.publication_status === 'published' ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 dark:text-gray-400">Type:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-gray-50 capitalize">
                      {previewData.assessment_type?.replace('_', ' ') || 'Fixed Form'}
                    </span>
                  </div>
                </div>
                {previewData.description && (
                  <div className="mt-4 border-t border-gray-100 pt-4 dark:border-white/[0.06]">
                    <p className="text-[13.5px] leading-relaxed text-gray-700 dark:text-gray-300">{previewData.description}</p>
                  </div>
                )}
              </div>

              {/* Survey Structure */}
              {previewData.survey_detail && (
                <div className="border-t border-gray-100 pt-5 dark:border-white/[0.06]">
                  <div className="mb-3 flex items-center gap-2">
                    <Layers className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Survey Structure</h4>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl border border-gray-200/80 p-4 dark:border-white/[0.07]">
                      <div className="flex items-center justify-between">
                        <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400">Total Screens</span>
                        <span className="text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-50">
                          {previewData.survey_detail.screens?.length || 0}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Assessment pages</p>
                    </div>

                    <div className="rounded-xl border border-gray-200/80 p-4 dark:border-white/[0.07]">
                      <div className="flex items-center justify-between">
                        <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400">Total Items</span>
                        <span className="text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-50">
                          {previewData.question_count || 0}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Questions & items</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Screen Details */}
              {previewData.survey_detail?.screens && previewData.survey_detail.screens.length > 0 && (
                <div className="border-t border-gray-100 pt-5 dark:border-white/[0.06]">
                  <div className="mb-3 flex items-center gap-2">
                    <ListChecks className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Screen Breakdown</h4>
                  </div>

                  <div className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-xl border border-gray-200/80 dark:divide-white/[0.06] dark:border-white/[0.07]">
                    {previewData.survey_detail.screens.map((screen, index) => (
                      <div
                        key={screen.id || index}
                        className="flex items-center justify-between gap-3 px-3.5 py-2.5"
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="w-6 flex-shrink-0 text-center">
                            <span className="text-[13px] font-medium tabular-nums text-gray-400 dark:text-gray-500">
                              {screen.order_number || index + 1}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[13.5px] font-medium text-gray-900 dark:text-gray-50">
                              {screen.title || `Screen ${index + 1}`}
                            </p>
                            {screen.description && (
                              <p className="mt-0.5 max-w-md truncate text-xs text-gray-500 dark:text-gray-400">
                                {screen.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-shrink-0 items-center text-sm">
                          <span className="badge badge-gray tabular-nums">
                            {screen.elements?.filter(el => el.type === 'question')?.length || 0} items
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional Metadata */}
              {(previewData.therapeutic_area || previewData.created_at) && (
                <div className="border-t border-gray-100 pt-5 dark:border-white/[0.06]">
                  <div className="grid grid-cols-2 gap-4 text-[13px]">
                    {previewData.therapeutic_area && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Therapeutic Area:</span>
                        <span className="ml-2 font-medium text-gray-900 dark:text-gray-50">{previewData.therapeutic_area}</span>
                      </div>
                    )}
                    {previewData.created_at && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Created:</span>
                        <span className="ml-2 font-medium tabular-nums text-gray-900 dark:text-gray-50">
                          {new Date(previewData.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-white/[0.06] dark:bg-white/[0.02]">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="btn btn-secondary"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  // Assessment is already selected, user can now proceed to copy
                }}
                className="btn btn-primary"
              >
                <Copy className="h-4 w-4" />
                Use This Assessment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Name Rename Modal */}
      {showRenameModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 dark:bg-black/70">
          <div className="mx-4 w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
            <div className="border-b border-gray-100 px-6 py-4 dark:border-white/[0.06]">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">Assessment Name Already Exists</h3>
                <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400">Please enter a different name</p>
              </div>
            </div>

            <div className="space-y-4 px-6 py-5">
              {duplicateNameData?.userProvidedName ? (
                <div className="space-y-2">
                  <p className="text-[13.5px] text-gray-700 dark:text-gray-300">
                    The name you provided (<strong className="font-semibold">"{duplicateNameData?.originalName}"</strong>) already exists in this library.
                  </p>
                  <p className="text-[13.5px] text-gray-500 dark:text-gray-400">
                    Please choose a different name to continue with the import.
                  </p>
                </div>
              ) : (
                <p className="text-[13.5px] text-gray-700 dark:text-gray-300">
                  An assessment named <strong className="font-semibold">"{duplicateNameData?.originalName}"</strong> already exists in this library.
                  Please provide a unique name for the imported assessment.
                </p>
              )}

              <div>
                <label className="label">
                  New Assessment Name *
                </label>
                <input
                  type="text"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  placeholder="Enter a unique name"
                  className="input"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && renameValue.trim()) {
                      handleRetryWithNewName();
                    }
                  }}
                />
              </div>

              {uploadError && (
                <p className="text-[13px] text-red-600 dark:text-red-400">{uploadError}</p>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-white/[0.06] dark:bg-white/[0.02]">
              <button
                onClick={() => {
                  setShowRenameModal(false);
                  setDuplicateNameData(null);
                  setRenameValue('');
                  setUploadError('');
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleRetryWithNewName}
                disabled={!renameValue.trim() || uploading}
                className="btn btn-primary"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Import with New Name
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LibrarySelection;
